import { call, delay, put, race, take, takeLatest } from 'redux-saga/effects';
import { captureException } from '@sentry/nextjs';
import ActionTypes from '../../../../Data/ActionTypes';
import JourneyOutcome from '../../../../Data/JourneyOutcome';
import JourneyOutcomeDetail from '../../../../Data/JourneyOutcomeDetail';
import { incrementMetric } from '../../../../Services/Datadog';
import { getErrorTypeTag } from '../clientApiError.helpers';
import reportJourneyOutcomeOnce from '../reportJourneyOutcomeOnce';
import {
  setInactivityPromptOpen,
  setInactivityCountdownSeconds,
  setJourneyTimedOut,
} from '../../slices/clientApiJourney';

const IDLE_TIMEOUT_SECONDS = 75;
const COUNTDOWN_SECONDS = 45;

/**
 * Prompts after an idle spell, then ends the journey unless the user confirms.
 *
 * Racing each wait against its cancelling action is what gives reset-on-activity
 * and confirm-mid-countdown without tracking timestamps.
 */
export function* clientApiInactivityTimerWorker() {
  while (true) {
    const { activity } = yield race({
      activity: take(ActionTypes.CLIENT_API_USER_ACTIVITY),
      idle: delay(IDLE_TIMEOUT_SECONDS * 1000),
    });

    if (!activity) {
      // Separate dispatches, so opening first would render a stale countdown.
      yield put(
        setInactivityCountdownSeconds({
          inactivityCountdownSeconds: COUNTDOWN_SECONDS,
        })
      );

      let remainingSeconds = COUNTDOWN_SECONDS;
      let isConfirmed = false;

      // The prompt has no dismiss of its own, so closing it must survive this
      // worker being cancelled: a stop mid-countdown would otherwise strand it
      // open with a dead button.
      try {
        yield put(setInactivityPromptOpen({ isInactivityPromptOpen: true }));

        while (remainingSeconds > 0 && !isConfirmed) {
          const { confirmed } = yield race({
            confirmed: take(ActionTypes.CONFIRM_CLIENT_API_STILL_PRESENT),
            tick: delay(1000),
          });

          if (confirmed) {
            isConfirmed = true;
          } else {
            remainingSeconds -= 1;
            yield put(
              setInactivityCountdownSeconds({
                inactivityCountdownSeconds: remainingSeconds,
              })
            );
          }
        }
      } finally {
        yield put(setInactivityPromptOpen({ isInactivityPromptOpen: false }));
      }

      if (!isConfirmed) {
        // Swallowed so a failed send still moves the user on.
        try {
          yield call(reportJourneyOutcomeOnce, {
            outcome: JourneyOutcome.TIMEOUT,
            outcomeDetail: JourneyOutcomeDetail.INACTIVITY_CUTOFF,
          });
        } catch (error) {
          incrementMetric({
            name: 'clientApiInactivityTimeoutOutcomeError',
            tags: { type: getErrorTypeTag(error) },
          });
          captureException(error);
        }

        yield put(setJourneyTimedOut({ journeyTimedOut: true }));
        return;
      }
    }
  }
}

export function* runUntilStopped() {
  yield race({
    task: call(clientApiInactivityTimerWorker),
    stopped: take(ActionTypes.STOP_CLIENT_API_INACTIVITY_TIMER),
  });
}

export default function* clientApiInactivityTimerWatcher() {
  yield takeLatest(
    ActionTypes.START_CLIENT_API_INACTIVITY_TIMER,
    runUntilStopped
  );
}
